import Token, { type TokenClaims } from '@/data/auth/model';

import Cookies from 'js-cookie';
import { jwtDecode } from 'jwt-decode';

const setCookie = (name: string, value: string, options: Cookies.CookieAttributes = {}) => {
  const params = {
    domain: CONFIG.COOKIE_DOMAIN,
    secure: true,
    sameSite: 'Lax',
    ...options,
  };

  return Cookies.set(name, value, params);
};

export const removeCookie = (name: string) => {
  return Cookies.remove(name, { domain: CONFIG.COOKIE_DOMAIN });
};

export const getToken = () => Cookies.get('W-Token');

export const getBearerToken = () => {
  const cookieValue = Cookies.get('W-Token');
  return cookieValue ? (cookieValue.startsWith('Bearer ') ? cookieValue : `Bearer ${cookieValue}`) : '';
};

export const setToken = (token: string, options: Cookies.CookieAttributes = {}) => {
  return setCookie('W-Token', token, options);
};

export const removeToken = () => {
  return removeCookie('W-Token');
};

export const setUserId = (userId: string, options: Cookies.CookieAttributes = {}) => {
  return setCookie('W-UserId', userId, options);
};

export const removeUserId = () => {
  return removeCookie('W-UserId');
};

export const getTokenInfo = () => {
  const token = getToken();

  const claims = token ? jwtDecode<TokenClaims>(token) : ({} as TokenClaims);
  return new Token(claims);
};

export const getUserId = () => {
  const cookieValue = Cookies.get('W-UserId');

  return cookieValue ? Number.parseInt(cookieValue, 10) : Number.NaN;
};

export const getStepUpToken = () => {
  return Cookies.get('W-StepUpToken');
};

export const setStepUpToken = (token: string, options = {}) => {
  const params = {
    domain: CONFIG.COOKIE_DOMAIN,
    ...options,
  };

  return Cookies.set('W-StepUpToken', token, params);
};

export function removeStepUpToken() {
  return removeCookie('W-StepUpToken');
}

export function getStepUpTokenInfo() {
  const token = getStepUpToken();
  if (!token) {
    return undefined;
  }

  const claims = token ? jwtDecode<TokenClaims>(token) : ({} as TokenClaims);
  return new Token(claims);
}

export const getAnonymousUserId = () => Cookies.get('ld_anon_context');

// Returns true when the current session is a WIW employee impersonating another
// user via the internal x-ray/autologin tool. Detects the `assumer`/`internal`
// claims stamped on the JWT by the login service's CreateAutologin RPC.
export function isImpersonatingSession(): boolean {
  return getTokenInfo().isImpersonating();
}
